import { z } from 'zod';

/**
 * Catálogo de notificações de **operação** para super-admins.
 *
 * Distinto de `notificationKinds.ts` (preferências de notificação por-membro
 * de um tenant, no `apps/app`/Android). Este catálogo é a fonte única dos
 * eventos de negócio que a operação (super-admins do `apps/admin`) quer
 * acompanhar — aquisição, receita, churn e risco — com dois canais
 * independentes por evento: e-mail e push web.
 *
 * Fonte única espelhada em:
 *  - Cloud Functions (`apps/functions/src/lib/adminNotify.ts`) — o helper
 *    `notifyAdmins(event, …)` lista os super-admins, lê as preferências de
 *    cada um (`/adminNotificationPrefs/{uid}`) e faz o fan-out por canal.
 *  - Web admin (`apps/admin`) — `NotificationPrefsPage` lista um par de
 *    toggles (e-mail/push) por evento e persiste o doc.
 *
 * A preferência é armazenada por super-admin em `/adminNotificationPrefs/{uid}`
 * (coleção top-level; Rules garantem que cada admin só escreve o próprio doc).
 * Evento/canal ausente no doc → default aplicado no read via
 * `resolveAdminNotificationPref` (não há backfill no Firestore).
 */

/**
 * Eventos de operação, na ordem em que aparecem na tela de preferências.
 *
 * - `NEW_TENANT` — uma nova empresa (tenant) foi criada.
 * - `PAYMENT_RECEIVED` — assinatura ativada (primeiro pagamento confirmado).
 *   NÃO dispara em renovações recorrentes (ruído) — só na ativação inicial.
 * - `CANCELLATION_REFUND` — assinatura cancelada ou reembolso MBG concluído.
 * - `PAYMENT_FAILED_SUSPENDED` — pagamento falhou e o tenant foi suspenso por
 *   inadimplência.
 * - `BILLING_ANOMALY` — divergência gateway×local detectada por um job de
 *   billing (ex.: webhook da Stripe perdido além da janela de retry, com o
 *   inadimplente ainda com acesso; #130). Exige reconciliação manual
 *   (`reconcileSubscriptionFromGateway`).
 * - `DUPLICATE_SUBSCRIPTION` — o mesmo tenant completou DOIS checkouts e ficou
 *   com duas assinaturas vivas. O webhook cancela a duplicata na hora
 *   (§3.1.6), mas a primeira fatura de CADA uma já foi cobrada: alguém precisa
 *   reembolsar. Só um `logger.warn` guardava isso, e log não acorda ninguém.
 * - `CHECKOUT_ABANDONED` — o cliente abriu o checkout e NÃO pagou: a sessão
 *   expirou ou o meio de pagamento falhou (tipicamente o QR do Pix vencendo).
 *   É lead quente esfriando — chega enquanto ainda dá para ligar, e traz a URL
 *   de recuperação quando existe (#289). Diferente de
 *   `PAYMENT_FAILED_SUSPENDED`, que é cliente JÁ pagante ficando inadimplente.
 */
export const ADMIN_NOTIFICATION_EVENTS = [
  'NEW_TENANT',
  'PAYMENT_RECEIVED',
  'CANCELLATION_REFUND',
  'PAYMENT_FAILED_SUSPENDED',
  'BILLING_ANOMALY',
  'CHECKOUT_ABANDONED',
  'DUPLICATE_SUBSCRIPTION',
] as const;

export type AdminNotificationEvent = (typeof ADMIN_NOTIFICATION_EVENTS)[number];

export const AdminNotificationEventSchema = z.enum(ADMIN_NOTIFICATION_EVENTS);

/** Canais de entrega de uma notificação de operação. */
export const ADMIN_NOTIFICATION_CHANNELS = ['email', 'push'] as const;
export type AdminNotificationChannel = (typeof ADMIN_NOTIFICATION_CHANNELS)[number];

/**
 * Estado de um evento: cada canal ligado/desligado. O cliente sempre escreve
 * os dois campos; a leitura tolera ausência (default aplicado).
 */
export const AdminNotificationChannelPrefSchema = z.object({
  email: z.boolean(),
  push: z.boolean(),
});
export type AdminNotificationChannelPref = z.infer<typeof AdminNotificationChannelPrefSchema>;

/**
 * Default quando o super-admin nunca configurou (doc ou campo ausente):
 * **tudo ligado**. Ausência de preferência = opt-in total — o admin recebe
 * tudo até desligar explicitamente.
 */
export const ADMIN_NOTIFICATION_DEFAULTS: AdminNotificationChannelPref = {
  email: true,
  push: true,
};

/**
 * Documento `/adminNotificationPrefs/{uid}`.
 *
 * `events` é um mapa parcial evento → preferência por-canal (também parcial,
 * por tolerância de leitura). `updatedAt` é Firestore `Timestamp` (tipado
 * `unknown`; cliente materializa em millis ao ler, como `ContactSubmission`).
 */
export const AdminNotificationPrefsSchema = z.object({
  // `partialRecord` (não `record`): no Zod v4 `z.record(enum, …)` é exaustivo
  // — exigiria todas as chaves de evento presentes. Aqui o mapa é parcial em
  // ambos os níveis (evento e canal ausentes caem no default no read).
  events: z
    .partialRecord(AdminNotificationEventSchema, AdminNotificationChannelPrefSchema.partial())
    .optional(),
  updatedAt: z.unknown().optional(),
});
export type AdminNotificationPrefs = z.infer<typeof AdminNotificationPrefsSchema>;

/** Mapa de preferências por evento (parcial em ambos os níveis). */
export type AdminNotificationPrefsMap = AdminNotificationPrefs['events'];

/**
 * Resolve a preferência de um evento aplicando os defaults aos canais ausentes.
 * Server e cliente usam isto no read — não há backfill no Firestore.
 */
export function resolveAdminNotificationPref(
  event: AdminNotificationEvent,
  prefs: AdminNotificationPrefsMap | null | undefined,
): AdminNotificationChannelPref {
  const entry = prefs?.[event] ?? {};
  return {
    email: entry.email ?? ADMIN_NOTIFICATION_DEFAULTS.email,
    push: entry.push ?? ADMIN_NOTIFICATION_DEFAULTS.push,
  };
}

/**
 * `true` se o super-admin deve receber a notificação naquele canal —
 * preferência não desligada (ausente conta como default = ligado).
 */
export function shouldNotifyAdmin(
  event: AdminNotificationEvent,
  channel: AdminNotificationChannel,
  prefs: AdminNotificationPrefsMap | null | undefined,
): boolean {
  return resolveAdminNotificationPref(event, prefs)[channel];
}
